import { Injectable } from '@angular/core';
import { RapidReviewGandalfService } from '@gandalf/services';
import { map, tap } from 'rxjs/operators';
import { RapidReviewAssessmentAndPlanResponse } from '@gandalf/model/rapid-review-assessment-and-plan-response';
import { RapidReviewPerformedServiceResponse } from '@gandalf/model/rapid-review-performed-service-response';
import { RapidReviewWorkflowTestResponse } from '@gandalf/model/rapid-review-workflow-test-response';
import { RapidReviewFieldValueResponse } from '@gandalf/model/rapid-review-field-value-response';
import { RapidReviewHistoryResponse } from '@gandalf/model/rapid-review-history-response';
import { RapidReviewExamRecapResponse } from '@gandalf/model/rapid-review-exam-recap-response';
import { DiagnosisService } from '@core/diagnosis/diagnosis.service';
import { RapidReviewDiagnosisResponse } from '@gandalf/model/rapid-review-diagnosis-response';
import { merge, MonoTypeOperatorFunction, Subject } from 'rxjs';
import { SortingUtil } from 'majora';
import { _isNil } from '@core/lodash/lodash';
import { EnumUtil } from 'morgana';
import { EncounterStatus } from '@gandalf/constants';
import { FeatureService } from '@core/feature/feature.service';
import { FEATURE_FLAGS } from '@core/feature/feature.constants';
import { EventsManagerService } from '@core/events-manager/events-manager.service';
import { Observable } from 'rxjs/internal/Observable';
import { INTER_APP_CONSTANTS } from '@core/legacy/inter-app.constants';
import { PrescriptionUtil } from '@core/prescription-util/prescription-util';
import { RapidReviewRefractionResponse } from '@gandalf/model/rapid-review-refraction-response';
import { RapidReviewOdOsResponse } from '@gandalf/model/rapid-review-od-os-response';
import { RapidReviewAllergyResponse } from '@gandalf/model/rapid-review-allergy-response';
import { RapidReviewPresentIllnessResponse } from '@gandalf/model/rapid-review-present-illness-response';
import { RapidReviewAcuityResponse } from '@gandalf/model/rapid-review-acuity-response';
import { RapidReviewAcuityEyesResponse } from '@gandalf/model/rapid-review-acuity-eyes-response';
import { RapidReviewModalData } from '@shared/component/rapid-review-modal/rapid-review-modal.component';

export interface RapidReviewAssessmentAndPlanFormattedResponse extends RapidReviewAssessmentAndPlanResponse {
	formattedServices: RapidReviewPerformedServiceFormattedResponse[];
}

export interface RapidReviewPerformedServiceFormattedResponse extends RapidReviewPerformedServiceResponse {
	diagnosisCodes: string;
}

export interface RapidReviewExamRecapFormattedResponse extends RapidReviewExamRecapResponse {
	entranceTests: RapidReviewTestFormattedResponse[];
	specialtyTests: RapidReviewTestFormattedResponse[];
	medicalTests: RapidReviewTestFormattedResponse[];
}

export interface RapidReviewTestFormattedResponse extends RapidReviewWorkflowTestResponse {
	formattedFields: RapidReviewFieldFormattedResponse[];
}

export interface RapidReviewFieldFormattedResponse extends RapidReviewFieldValueResponse {
	subName: string;
	subNameClass: string;
	valueClass: string;
	additionalName: string;
	additionalNameClass: string;
	additionalValue: string;
	additionalValueClass: string;
}

export interface RapidReviewHistoryFormattedResponse extends RapidReviewHistoryResponse {
	formattedPresentIllnesses: string;
	formattedReviewOfSystems: string;
	formattedMedications: string;
	formattedAllergies: string;
	formattedOcularHistory: string;
	formattedFamilyHistory: string;
	formattedAcuityFields: RapidReviewAcuityFieldFormattedResponse[];
}

export interface RapidReviewAcuityFieldFormattedResponse {
	fieldName: string;
	uncorrected: RapidReviewAcuityValueFormattedResponse;
	aided: RapidReviewAcuityValueFormattedResponse;
}

export interface RapidReviewAcuityValueFormattedResponse {
	value: string;
	od: string;
	os: string;
	ou: string;
}

const ENTRANCE_TEST_CATEGORY_IDS: number[] = [3104, 3125, 3123, 3105, 3103, 3100, 3117, 3107, 3101, 3108, 3122, 3106];
const MEDICAL_TESTING_CATEGORY_IDS: number[] = [3109, 3112, 3114, 3110, 3121, 3102, 3111];

@Injectable({
	providedIn: 'root',
})
export class RapidReviewModalService {

	// number of requests that are currently loading
	private loadingCount = 0;
	private openSubject = new Subject<number | RapidReviewModalData>();
	readonly opened: Observable<number | RapidReviewModalData> = merge(
		this.openSubject.asObservable(),
		this.eventManagerService.subscribeObservableArgument(INTER_APP_CONSTANTS.REV360.PATIENTS.ENCOUNTER_RAPID_REVIEW),
	);

	constructor(
		private rapidReviewGandalfService: RapidReviewGandalfService,
		private diagnosisService: DiagnosisService,
		private featureService: FeatureService,
		private eventManagerService: EventsManagerService,
	) {
	}

	canOpen(encounterStatus: EncounterStatus, fallbackEncounterId: number = undefined) {
		return this.featureService.isFeatureOn(FEATURE_FLAGS.FEATURES.ENCOUNTERS.RAPID_REVIEW)
			&& (
				(!_isNil(encounterStatus) && !EnumUtil.equals(encounterStatus, EncounterStatus.CANCELED))
				|| !_isNil(fallbackEncounterId)
			);
	}

	open(data: number | RapidReviewModalData) {
		// AppComponent will actually open the modal since TypeSafeModalManagerService cannot be injected here
		this.openSubject.next(data);
	}

	getRapidReviewHeader(encounterId: number) {
		return this.rapidReviewGandalfService.getRapidReviewHeader(encounterId).pipe(
			this.loading(),
			map(response => {
				response.encounters = SortingUtil.sortBy(response.encounters, [e => e.encounterDate, e => e.encounterId], ['desc', 'desc']);
				response.insurances = SortingUtil.sortBy(response.insurances, [i => i.type, i => i.name, i => i.personInsuranceId], ['asc', 'asc', 'asc']);
				return response;
			}),
		);
	}

	getRapidReviewExamRecap(encounterId: number) {
		return this.rapidReviewGandalfService.getRapidReviewExamRecap(encounterId).pipe(
			this.loading(),
			map(response => {
				const formatted = response as RapidReviewExamRecapFormattedResponse;
				formatted.eyeglassPrescriptions = SortingUtil.sortBy(
					response.eyeglassPrescriptions,
					[e => e.eyeglassPrescriptionId],
					['asc'],
				);
				formatted.contactLensPrescriptions = SortingUtil.sortBy(
					response.contactLensPrescriptions,
					[c => c.contactLensPrescriptionId],
					['asc'],
				);
				formatted.contactLensTrials = SortingUtil.sortBy(
					response.contactLensTrials,
					[t => t.contactLensTrialId],
					['asc'],
				);

				const entranceTests = response.worflowTests
					.filter(test => ENTRANCE_TEST_CATEGORY_IDS.includes(test.masterCategoryId));
				formatted.entranceTests = this.formatAndSortTests(entranceTests);

				const medicalTests = response.worflowTests
					.filter(test => MEDICAL_TESTING_CATEGORY_IDS.includes(test.masterCategoryId));
				formatted.medicalTests = this.formatAndSortTests(medicalTests);

				const specialtyTests = response.worflowTests
					.filter(test => !ENTRANCE_TEST_CATEGORY_IDS.includes(test.masterCategoryId) && !MEDICAL_TESTING_CATEGORY_IDS.includes(test.masterCategoryId));
				formatted.specialtyTests = this.formatAndSortTests(specialtyTests);

				return formatted;
			}),
		);
	}

	private formatAndSortTests(tests: RapidReviewWorkflowTestResponse[]) {
		return SortingUtil.sortBy(
			tests.map(test => this.formatTest(test)),
			[t => t.stepDisplayOrder, t => t.screenDisplayOrder, t => t.testPosition.top, t => t.testPosition.left, t => t.workflowTestId],
			['asc', 'asc', 'asc', 'asc', 'asc'],
		);
	}

	getRapidReviewAssessmentAndPlan(encounterId: number) {
		return this.rapidReviewGandalfService.getRapidReviewAssessmentAndPlan(encounterId).pipe(
			this.loading(),
			map(response => {
				const formatted = response as RapidReviewAssessmentAndPlanFormattedResponse;
				formatted.formattedServices = this.formatServices(response.performedServices);
				formatted.personDiagnoses = this.formatDiagnoses(response.personDiagnoses);
				return formatted;
			}),
		);
	}

	getRapidReviewHistory(encounterId: number) {
		return this.rapidReviewGandalfService.getRapidReviewHistory(encounterId).pipe(
			this.loading(),
			map(response => {
				const formatted = response as RapidReviewHistoryFormattedResponse;
				if (formatted.reasonForVisit) {
					formatted.reasonForVisit.historyDiagnoses = this.formatDiagnoses(response.reasonForVisit.historyDiagnoses);
				}
				formatted.formattedPresentIllnesses = SortingUtil.sortBy(response.presentIllnesses, [p => p.presentIllnessId], ['asc'])
					.map(hpi => this.formatHpi(hpi))
					.join(', ');
				formatted.formattedReviewOfSystems = response.reviewOfSystems?.details?.join(', ');
				formatted.formattedMedications = SortingUtil.sortBy(response.medications, [m => m.medicationPrescriptionId], ['asc'])
					.map(med => med.description).join(', ');
				formatted.formattedAllergies = SortingUtil.sortBy(response.allergies, [a => a.allergyId], ['asc'])
					.map(allergy => this.formatAllergy(allergy))
					.join(', ');
				formatted.formattedOcularHistory = response.ocularHistory?.details?.join(', ');
				formatted.formattedFamilyHistory = response.familyHistory?.details?.join(', ');
				formatted.formattedAcuityFields = this.formatAcuityFields(response.uncorrectedVisualAcuity, response.aidedVisualAcuity);
				return formatted;
			}),
		);
	}

	formatAcuityFields(uncorrected: RapidReviewAcuityResponse, aided: RapidReviewAcuityResponse) {
		return [
			this.formatAcuityField('Near @', uncorrected?.nearDistanceVa, aided?.nearDistanceVa),
			this.formatAcuityEyesField('Distance VA', uncorrected?.distanceVa, aided?.distanceVa),
			this.formatAcuityEyesField('Near VA', uncorrected?.nearVa, aided?.nearVa),
			this.formatAcuityField('VA Method', uncorrected?.vaMethod, aided?.vaMethod),
			this.formatAcuityField('Rx Worn', uncorrected?.rxWorn, aided?.rxWorn),
			this.formatAcuityEyesField('Pinhole VA', uncorrected?.pinholeVa, aided?.pinholeVa),
			this.formatAcuityField('Comment', uncorrected?.comment, aided?.comment),
		].filter(field => !_isNil(field));
	}

	formatAcuityField(fieldName: string, uncorrectedValue: string, aidedValue: string) {
		if (uncorrectedValue || aidedValue) {
			return {fieldName, uncorrected: {value: uncorrectedValue}, aided: {value: aidedValue}} as RapidReviewAcuityFieldFormattedResponse;
		} else {
			return null;
		}
	}

	formatAcuityEyesField(fieldName: string, uncorrectedEyes: RapidReviewAcuityEyesResponse, aidedEyes: RapidReviewAcuityEyesResponse) {
		if (uncorrectedEyes || aidedEyes) {
			return {
				fieldName,
				uncorrected: uncorrectedEyes ? this.formatAcuityEyes(uncorrectedEyes) : null,
				aided: aidedEyes ? this.formatAcuityEyes(aidedEyes) : null,
			} as RapidReviewAcuityFieldFormattedResponse;
		} else {
			return null;
		}
	}

	formatAcuityEyes(eyes: RapidReviewAcuityEyesResponse) {
		return {
			od: this.formatAcuity(eyes.od, eyes.odModifier),
			os: this.formatAcuity(eyes.os, eyes.osModifier),
			ou: this.formatAcuity(eyes.ou, eyes.ouModifier),
		} as RapidReviewAcuityValueFormattedResponse;
	}

	formatAcuity(acuity: string, modifier: string) {
		let formatted = '';
		if (acuity) {
			formatted += `20/${acuity} `;
		}
		if (modifier) {
			formatted += modifier;
		}
		return formatted.trim();
	}

	formatServices(services: RapidReviewPerformedServiceResponse[]) {
		return SortingUtil.sortBy(services, [s => s.performedServiceId], ['asc']).map(service => {
			const formattedService = service as RapidReviewPerformedServiceFormattedResponse;
			formattedService.diagnoses = SortingUtil.sortBy(service.diagnoses, [d => d.personDiagnosisId], ['asc']);
			formattedService.diagnosisCodes = formattedService.diagnoses.map(d => d.code).join(', ');
			return formattedService;
		});
	}

	formatDiagnoses(diagnoses: RapidReviewDiagnosisResponse[]) {
		diagnoses = SortingUtil.sortBy(
			diagnoses,
			[d => d.diagnosisDate, d => this.diagnosisService.lookupDiagnosisCodeSetOrder(d.codeSet), d => d.personDiagnosisId],
			['desc', 'asc', 'desc'],
		);
		diagnoses.forEach(d => d.carePlanItems = SortingUtil.sortBy(d.carePlanItems, [c => c.carePlanItemId], ['asc']));
		return diagnoses;
	}

	formatHpi(hpi: RapidReviewPresentIllnessResponse) {
		return hpi.details.length > 0 ? `${hpi.diagnosis} (${hpi.details.join(', ')})` : hpi.diagnosis;
	}

	formatAllergy(allergy: RapidReviewAllergyResponse) {
		const reactions = SortingUtil.sortBy(allergy.reactions, [r => r.sortOrder, r => r.id], ['asc', 'asc'])
			.map(r => r.value)
			.join(', ');
		return reactions ? `${allergy.name} (${reactions})` : allergy.name;
	}

	formatTest(test: RapidReviewWorkflowTestResponse) {
		const formattedTest = test as RapidReviewTestFormattedResponse;
		// combine refraction, od/os and other fields into a single list and then sort by position
		const formattedFields: RapidReviewFieldFormattedResponse[] = [];
		test.refractions.forEach(refraction => {
			if (refraction.od) {
				formattedFields.push(this.formatRefractionEye(test, refraction, true));
			}
			if (refraction.os) {
				formattedFields.push(this.formatRefractionEye(test, refraction, false));
			}
		});

		const pairs: Array<[string, string, string]> = [
			['Distance VA (20/)', 'Distance VA Modifier', ''],
			['OU Distance VA (20/)', 'OU Distance VA Modifier', ''],
			['Distance VA (6/)', 'Distance VA Modifier', ''],
			['OU Distance VA (6/)', 'OU Distance VA Modifier', ''],
			['Near Add VA (20/)', 'Near Add VA Modifier', ''],
			['OU Near Add VA (20/)', 'OU Near Add VA Modifier', ''],
			['Near Add VA (0.4/)', 'Near Add VA Modifier', ''],
			['Distance VA', 'Distance Numerator', 'Distance VA Modifier'],
			['OU Distance VA', 'OU Distance Numerator', 'OU Distance VA Modifier'],
			['Near Add VA', 'Add Distance Numerator', 'Near Add VA Modifier'],
			['OU Near Add VA', 'OU Add Distance Numerator', 'OU Near Add VA Modifier'],
		];

		const combinedOdOsFields: RapidReviewOdOsResponse[] = pairs.reduce(
			(acc, [base, modifier, numerator]) => this.combineFields(acc, base, modifier, numerator),
			[...test.odOsFields],
		);

		const combinedOtherFields: RapidReviewFieldValueResponse[] = pairs.reduce(
			(acc, [base, modifier, numerator]) => this.combineOtherFields(acc, base, modifier, numerator),
			[...test.otherFields],
		);

		formattedFields.push(...combinedOdOsFields.map(f => this.formatOdOsField(f)));
		formattedFields.push(...combinedOtherFields.map(f => this.formatLabelAndPrefix(f)).map(f => this.formatOtherField(f)));
		formattedTest.formattedFields = SortingUtil.sortBy(
			formattedFields,
			[
				f => f?.position?.top ?? 0,
				f => f?.position?.left ?? 0,
				f => f?.fieldValueId ?? 0,
			],
			['asc', 'asc', 'asc'],
		);
		return formattedTest;
	}

	private formatLabelAndPrefix(field: RapidReviewFieldValueResponse) {
		const match = field?.name?.match(/\(([\d.]+\/)\)/);
		const prefix = match ? match[1] : ''; // ex: "20/", "6/", "0.4/"
		const addPrefix = (value: string) => prefix ? `${prefix}${value}` : value;
		field.value = addPrefix(field.value);
		field.name = field?.name?.replace(' (' + prefix + ')', '');
		return field;
	}

	combineOtherFields(
		fields: RapidReviewFieldValueResponse[],
		baseLabel: string,
		modifierLabel: string,
		numeratorLabel: string,
	): RapidReviewFieldValueResponse[] {
		const { [baseLabel]: base, [modifierLabel]: modifier, [numeratorLabel]: numerator } =
			this.getFieldsByLabel(fields, [baseLabel, modifierLabel, numeratorLabel]);
		if (!base || !modifier) {
			return fields;
		}

		const others = this.filterOtherFields(
			fields,
			[baseLabel, modifierLabel, numeratorLabel].filter(Boolean),
		);
		const formattedBase = this.formatLabelAndPrefix(base);
		return [
			...others,
			{
				...base,
				name: formattedBase.name,
				value: this.buildCombinedValue(
					formattedBase?.value,
					modifier?.value,
					numeratorLabel !== '',
					numerator?.value,
				),
			},
		];
	}

	combineFields(
		fields: RapidReviewOdOsResponse[],
		baseLabel: string,
		modifierLabel: string,
		numeratorLabel: string,
	): RapidReviewOdOsResponse[] {
		const { [baseLabel]: base, [modifierLabel]: modifier, [numeratorLabel]: numerator } =
			this.getFieldsByLabel(fields, [baseLabel, modifierLabel, numeratorLabel]);
		if (!base || !modifier) {
			return fields;
		}

		const others = this.filterOtherFields(
			fields,
			[baseLabel, modifierLabel, numeratorLabel].filter(Boolean),
		);
		return [
			...others,
			{
				...base,
				odField: {
					...base.odField,
					value: this.buildCombinedValue(
						base?.odField?.value,
						modifier?.odField?.value,
						numeratorLabel !== '',
						numerator?.odField?.value ?? '',
					),
				},
				osField: {
					...base.osField,
					value: this.buildCombinedValue(
						base?.osField?.value,
						modifier?.osField?.value,
						numeratorLabel !== '',
						numerator?.osField?.value ?? '',
					),
				},
			},
		];
	}

	private getFieldsByLabel<T extends { name: string }>(
		fields: T[],
		labels: string[],
	): Record<string, T | undefined> {
		return labels.reduce((acc, label) => {
			acc[label] = fields.find(f => f.name === label);
			return acc;
		}, {} as Record<string, T | undefined>);
	}

	private filterOtherFields<T extends { name: string }>(
		fields: T[],
		labelsToExclude: string[],
	): T[] {
		return fields.filter(f => !labelsToExclude.includes(f.name));
	}

	private buildCombinedValue(
		base?: string,
		modifier?: string,
		isTrial?: boolean,
		numerator?: string,
	): string {
		return isTrial
			? `${base ?? ''}/${modifier ?? ''} ${numerator ?? ''}`.trim()
			: `${base ?? ''} ${modifier ?? ''}`.trim();
	}

	formatRefractionEye(test: RapidReviewWorkflowTestResponse, refraction: RapidReviewRefractionResponse, useOd: boolean): RapidReviewFieldFormattedResponse {
		// only include refraction name if it is different from the test name; and remove the test name to avoid duplicate text
		const name = refraction.name !== test.name ? refraction.name.replace(test.name, '').trim() : '';
		// refractions are displayed in separate lines for OD and OS, so format only one at a time
		const eye = useOd ? refraction.od : refraction.os;
		// if OD refraction is present, place the OS refraction immediately after it by reusing the OD field position
		const fieldEye = refraction.od ?? eye;
		return {
			name,
			subName: useOd ? 'OD' : 'OS',
			subNameClass: useOd ? 'rapid-review-od' : 'rapid-review-os',
			value: PrescriptionUtil.formatPrescriptionValues(eye.sphere, eye.cylinder, eye.axis, null),
			position: fieldEye.fieldPosition,
			fieldValueId: fieldEye.fieldValueId,
		} as RapidReviewFieldFormattedResponse;
	}

	formatOdOsField(odOs: RapidReviewOdOsResponse): RapidReviewFieldFormattedResponse {
		const eye = odOs.odField ?? odOs.osField;
		const additional = odOs.odField && odOs.osField;
		const match = odOs.name.match(/\(([\d.]+\/)\)/);
		const prefix = match ? match[1] : ''; // ex: "20/", "6/", "0.4/"
		const addPrefix = (value: string) => prefix ? `${prefix}${value}` : value;
		return {
			name: odOs.name.replace(' (' + prefix + ')', ''),
			subName: odOs.odField ? 'OD' : 'OS',
			subNameClass: !odOs.odField ? 'rapid-review-os' : 'rapid-review-od',
			value: addPrefix(eye.value),
			valueClass: !eye.isDefaultNormal ? 'text-danger font-weight-bold' : null,
			position: eye.position,
			fieldValueId: eye.fieldValueId,
			// if both OD and OS values are present, then show OS value as an additional field on the same line
			additionalName: additional ? 'OS' : null,
			additionalNameClass: additional ? 'rapid-review-os' : null,
			additionalValue: additional ?  addPrefix(odOs.osField.value) : null,
			additionalValueClass: additional && !odOs.osField.isDefaultNormal ? 'text-danger font-weight-bold' : null,
		} as RapidReviewFieldFormattedResponse;
	}

	formatOtherField(field: RapidReviewFieldValueResponse): RapidReviewFieldFormattedResponse {
		return {
			...field,
			valueClass: !field.isDefaultNormal ? 'text-danger font-weight-bold' : null,
		} as RapidReviewFieldFormattedResponse;
	}

	/**
	 * Indicate a request is loading. Pass the results into .pipe() on the request Observable.
	 */
	loading<T>(): MonoTypeOperatorFunction<T> {
		this.loadingCount++;
		return tap({
			next: () => this.loadingCount--,
			error: () => this.loadingCount--,
		});
	}

	/**
	 * Returns true if there are any requests that are currently loading, false otherwise.
	 */
	isLoading() {
		return this.loadingCount > 0;
	}
}
